/**
 * Split a chat message into plain-text and link segments.
 *
 * Chat messages carry share links all the time — «Присоединяйтесь к комнате
 * https://agapella.io/room/QSJw4S» — and until now they rendered as dead text,
 * so the only way to follow one was to select and copy it by hand.
 *
 * Internal links (our own domains) are flagged separately: a link into the app
 * is a normal in-product action, while an outbound link deserves the usual
 * caution. Both are opened in a new tab on web so a half-written message, or a
 * live session in the background, is never thrown away by navigating the current
 * one.
 */

/** Domains that are us. Subdomains count (`app.agapella.io`, `app-ru.…`). */
const INTERNAL_DOMAINS = ['agapella.io', 'agape.live', 'agdev.tmless.org'];

// Deliberately conservative: http/https only. A bare `www.` or a naked domain is
// left as text rather than guessed at — mislabelling ordinary words as links is
// worse than missing one.
const URL_RE = /https?:\/\/[^\s<>()[\]{}«»"']+/gi;

export interface TextSegment {
  type: 'text';
  value: string;
}

export interface LinkSegment {
  type: 'link';
  value: string;
  /** Display form — trailing punctuation trimmed off. */
  label: string;
  /** True when the URL points at one of our own domains. */
  internal: boolean;
}

export type MessageSegment = TextSegment | LinkSegment;

/** Sentence punctuation that follows a URL rather than belonging to it. */
function trimTrailingPunctuation(url: string): { url: string; trailing: string } {
  const m = url.match(/[.,;:!?)»\]]+$/);
  if (!m) return { url, trailing: '' };
  return { url: url.slice(0, url.length - m[0].length), trailing: m[0] };
}

export function isInternalUrl(url: string): boolean {
  try {
    const host = new URL(url).hostname.toLowerCase();
    return INTERNAL_DOMAINS.some((d) => host === d || host.endsWith(`.${d}`));
  } catch {
    return false;
  }
}

/**
 * Segments in order. A message with no links yields a single text segment, so
 * callers can render the result unconditionally.
 */
export function linkifyText(text: string): MessageSegment[] {
  if (!text) return [{ type: 'text', value: '' }];

  const segments: MessageSegment[] = [];
  let lastIndex = 0;

  // Fresh regex state per call — a module-level /g regex is stateful.
  const re = new RegExp(URL_RE.source, 'gi');
  let match: RegExpExecArray | null;

  while ((match = re.exec(text)) !== null) {
    const raw = match[0];
    const { url, trailing } = trimTrailingPunctuation(raw);

    // A URL that is nothing but punctuation after trimming isn't a link.
    if (!/^https?:\/\/\S+/.test(url)) continue;

    if (match.index > lastIndex) {
      segments.push({ type: 'text', value: text.slice(lastIndex, match.index) });
    }

    segments.push({ type: 'link', value: url, label: url, internal: isInternalUrl(url) });

    if (trailing) segments.push({ type: 'text', value: trailing });
    lastIndex = match.index + raw.length;
  }

  if (lastIndex < text.length) {
    segments.push({ type: 'text', value: text.slice(lastIndex) });
  }

  return segments.length ? segments : [{ type: 'text', value: text }];
}
